import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    sidebarContainer: {
        width: '280px',
        flexShrink: 0,
        position: 'sticky',
        top: theme.spacing(3),
        maxHeight: 'calc(100vh - 48px)',
        overflowY: 'auto',
        paddingLeft: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
            position: 'relative',
            width: '100%',
            maxHeight: 'none',
            marginBottom: '1em',
            top: 0,
        },
    },
    tocTitle: {
        fontWeight: 'bold !important',
        marginBottom: theme.spacing(1),
        fontSize: '1rem',
        color: theme.palette.text.secondary,
    },
    tocItem: {
        padding: 0,
        paddingLeft: 4,
        paddingRight: 4,
        margin: 0,
        borderLeft: '2px solid transparent',
        color: theme.palette.primary.main,
        //textIndent: "1em hanging",
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    tocItemLevel1: { paddingLeft: "8px" },
    tocItemLevel2: { paddingLeft: "24px" },
    tocItemLevel3: { paddingLeft: "32px" },
    activeTocItem: {
        borderLeftColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
        '& .MuiListItemText-primary': {
            color: theme.palette.primary.secondary,
            fontWeight: "bold"
        },
    },
    tocText: {
        paddingLeft: "0.2em",
        '&:hover': {
            textDecoration: 'none',
        },
    }
}));

function TocListItem({ heading, activeId, onClick, minLevel }) {
    const level = (parseInt(heading.tagName.substring(1), 10) || 2) - (minLevel - 1);
    const classes = useStyles();
    const meRef = useRef(null);
    const isActive = activeId === heading.id;

   // useEffect(() => {
   //     if (isActive && meRef.current) {
      //      meRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        //}
  //  }, [isActive]);

    return (
        <ListItem ref={meRef} button dense onClick={_ => onClick(heading.id)} component='a' href={"#" + heading.id}
            className={`${classes.tocItem} ${classes["tocItemLevel"+level]} ${isActive ? classes.activeTocItem : ''}`}>
            <ListItemText primary={heading.text} classes={{ primary: classes.tocText }} onClick={e => e.preventDefault()} />
        </ListItem>
    );
}

export default function TableOfContentsSidebar({ headings }) {
    const classes = useStyles();
    const [activeId, setActiveId] = useState('');
    const isScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        if (!headings || headings.length === 0) return;
        const callback = (entries) => {
            //turn off while smooth scroll running
            if (isScrollingRef.current) return;

            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(callback, {
            rootMargin: '-80px 0px -40% 0px',
            threshold: 0.1,
        });

        headings.forEach((h) => {
            const el = document.getElementById(h.id);
            if (el) observer.observe(el);
        });

        return () => {
            observer.disconnect();
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        }
    }, [headings]);

    const handleLinkClick = (id) => {
        const el = document.getElementById(id);
        if (el) {
            isScrollingRef.current = true;
            setActiveId(id);

            el.scrollIntoView({ behavior: 'smooth', block: 'start' });

            const unlockScroll = () => {
                isScrollingRef.current = false;
                window.removeEventListener('scrollend', unlockScroll);
                if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            };

            window.addEventListener('scrollend', unlockScroll, { once: true });
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                if (isScrollingRef.current) {
                    unlockScroll();
                }
            }, 1500);
        }
    };

    if (!headings || headings.length === 0) return null;

    //adjust indent when starting at higher heading #
    const minLevel = Math.min(...headings.map(h => parseInt(h.tagName.substring(1), 10) || 2));
    return (
        <nav className={classes.sidebarContainer} aria-label="Table of Contents">
            <Typography className={classes.tocTitle} id="toc">Contents</Typography>
            <List style={{ padding: 0 }}>
                {headings.map((heading) => (
                    <TocListItem
                        minLevel={minLevel}
                        key={heading.id}
                        heading={heading}
                        activeId={activeId}
                        onClick={handleLinkClick}
                    />
                ))}
            </List>
        </nav>
    );
}